import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthContext } from '../../contexts/auth-context';
import { useShellContext } from '../../contexts/shell-context';
import { accessTokenStatus, type UserRole } from './claims';

/**
 * Guards de **navegação**.
 *
 * A fronteira de segurança continua a ser a RLS: estes componentes decidem que ecrã aparece, não
 * que dados o servidor devolve. Um guard contornado mostra uma tela vazia, porque as queries por
 * trás dela continuam a ser filtradas no Postgres.
 *
 * 🔴 Regra comum aos três: **enquanto `isLoading`, nenhum guard decide.** Decidir a meio é a causa
 * do logout falso — o utilizador autenticado dá refresh à página, o contexto ainda não resolveu a
 * sessão, e o guard já o expulsou para `/login`.
 */

/** Mostrado enquanto a sessão resolve. `role="status"` para o leitor de ecrã anunciar a espera. */
export function Aguardar() {
  const { t } = useTranslation();
  return (
    <p role="status" aria-live="polite">
      {t('state.loading')}
    </p>
  );
}

/**
 * Redireciona preservando a origem em `state.from`.
 *
 * Sem isto, quem abre um link profundo para uma página protegida aterra na home depois do login, e
 * perde o que tinha ido fazer. A T1.13 lê este `state` para devolver o utilizador ao destino.
 */
function IrPara({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={to} replace state={{ from: location.pathname }} />;
}

/**
 * Superfície **pública**: quem já tem sessão não vê Welcome, Login nem Signup.
 *
 * 🔴 Espera a sessão resolver antes de decidir, pela mesma razão que os guards privados: decidir a
 * meio faria o ecrã de boas-vindas piscar a cada refresh de quem já está autenticado — e piscar é
 * o sintoma visível de um guard que decidiu sem informação.
 *
 * Conta com sessão pendente de confirmação **não** é redirecionada: precisa de alcançar `/verify`,
 * e `useAuth` já a trata como não autenticada para efeitos de shell.
 *
 * 🔴 T1.17: conta em carência (`deleted_pending`) também **não** é redirecionada. O login recebe
 * primeiro uma sessão neutra e só a libera depois de reativar e renovar os claims; saltar para `/`
 * a meio mostraria uma rota privada a uma conta ainda não reativada.
 *
 * 🔴 T1.19 (/code-review): só redireciona quem o `AppRouter` trata como autenticado — claims
 * interativos, a mesma regra do `useAuth`. Email confirmado com token ainda não `active` (claims
 * por renovar, conta suspensa) ia para `/`, que na web mostra a landing a quem não tem shell: cada
 * "Entrar" voltava a `/` e o formulário ficava inalcançável.
 */
export function RedirectIfAuthenticated({ children, to = '/' }: { children: ReactNode; to?: string }) {
  const { session, user, claims, isLoading } = useAuthContext();

  if (isLoading) return <Aguardar />;
  if (
    session &&
    user?.email_confirmed_at &&
    claims.isInteractive &&
    claims.role !== null &&
    accessTokenStatus(session) !== 'deleted_pending'
  ) {
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, isLoading } = useAuthContext();

  if (isLoading) return <Aguardar />;
  if (!session) return <IrPara to="/login" />;
  return <>{children}</>;
}

/**
 * Exige email confirmado (`RF-AUTH-02`).
 *
 * O destino `/verify` só ganha ecrã na **T1.11**; a rota existe desde já, declarada no
 * `routes-manifest.ts`, para que a dívida seja visível e o teste falhe alto se alguém a remover.
 */
export function RequireVerifiedEmail({ children }: { children: ReactNode }) {
  const { session, user, isLoading } = useAuthContext();

  if (isLoading) return <Aguardar />;
  if (!session) return <IrPara to="/login" />;
  if (!user?.email_confirmed_at) return <IrPara to="/verify" />;
  return <>{children}</>;
}

/**
 * Exige um contexto profissional elegível para as rotas comuns de `/dashboard/*`.
 *
 * O onboarding é a única exceção: um negócio em `draft` precisa dessa rota para concluir a sua
 * criação. Negócios arquivados ou eliminados e utilizadores que escolheram o contexto consumidor
 * regressam à home do consumidor.
 */
export function RequireBusinessContext({ children }: { children: ReactNode }) {
  const { contexto, estadoNegocio, isLoading, falhaNegocio, recarregar } = useShellContext();
  const { claims } = useAuthContext();
  const location = useLocation();
  const { t } = useTranslation();

  if (isLoading) return <Aguardar />;

  // T1.19 (/code-review): leitura falhada não é "sem negócio" — redirecionar mandava o dono para
  // `/home` numa rede instável. Fica-se aqui, com a nova tentativa à mão.
  if (falhaNegocio) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <p role="alert" className="err">
          {t('state.businessLoadError')}
        </p>
        <button type="button" onClick={recarregar} className="btn btn-primary w-full">
          {t('actions.retry')}
        </button>
      </div>
    );
  }

  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  if (estadoNegocio === 'draft') {
    return pathname === '/dashboard/onboarding' ? (
      <>{children}</>
    ) : (
      <Navigate to="/dashboard/onboarding" replace />
    );
  }

  // T1.19b (§4.4): o onboarding é a porta de entrada de quem ainda não tem negócio — Entrada B e
  // upgrade "Tornar-me profissional". Só para `consumer`: o dono nasce aqui e só é promovido ao
  // iniciar o KYC (ADR 0037). Abrir o onboarding não concede nada; o negócio nasce `draft`.
  // `null` inclui o claim `business_id` ainda por atualizar após criar o rascunho; o onboarding lê
  // o rascunho no banco, logo retoma-o em vez de criar outro.
  if (!estadoNegocio && claims.role === 'consumer' && pathname === '/dashboard/onboarding') {
    return <>{children}</>;
  }

  if (!estadoNegocio || estadoNegocio === 'archived' || estadoNegocio === 'deleted') {
    return <Navigate to="/home" replace />;
  }

  if (contexto !== 'professional') return <Navigate to="/home" replace />;
  return <>{children}</>;
}

/**
 * Exige um dos papéis indicados.
 *
 * Papel insuficiente dá **403 legível**, não redirecionamento: mandar para a home quem clicou num
 * link de administração parece bug de navegação e gera bilhete de suporte. Dizer "não tem
 * permissão" é a informação correta.
 */
export function RequireRole({ roles, children }: { roles: readonly UserRole[]; children: ReactNode }) {
  const { session, claims, isLoading } = useAuthContext();
  const { t } = useTranslation();

  if (isLoading) return <Aguardar />;
  if (!session) return <IrPara to="/login" />;

  // `isInteractive` exclui `system`, que é papel de serviço: mesmo listado, nunca abre sessão de
  // interface (regra do parser da T1.8).
  const permitido = claims.isInteractive && claims.role !== null && roles.includes(claims.role);
  if (!permitido) {
    return <p role="alert">{t('errors:auth.forbidden')}</p>;
  }
  return <>{children}</>;
}
